import React from "react";
import styles from "./styles.module.scss";
import useDatafileIcons from "app/components/inputs/SelectBox/helpers/DatafileIcons";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { Loading } from "app/components/Wrappers";
import VideoAttachment from "./VideoAttachment";
import { MediaModel } from "app/models";
import AddMoreMedia from "../AddMoreMedia";
import { cls } from "app/utils";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface Props {
  uploadedMedia: MediaModel[];
  onClearMedia?: (index: string) => void;
  onAddMore?: () => void;
  hasPermission?: boolean;
  canSelectMedia?: boolean;
  onSelectMedia?: (media: MediaModel[]) => void;
  hasMultiSelect?: boolean;
  canRemoveMedia?: boolean;
  onReorderMedia?: (media: MediaModel[]) => void;
  alreadySelectedData?: MediaModel[];
}

function SortableMediaItem({
  media,
  renderImage,
  onClearMedia,
  canRemoveMedia,
  uploadError,
  onClearClick,
  cls,
  styles,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: media.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    boxShadow: isDragging ? "0 2px 8px rgba(0,0,0,0.15)" : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      data-id={media.id}
      {...attributes}
      {...listeners}
      className={cls(
        styles.preview_wrapper,
        isDragging && styles.dragging_clone,
        styles.grabbable,
      )}
      style={style}
    >
      {!uploadError && renderImage(media)}
      {onClearMedia && canRemoveMedia && (
        <span
          className={styles.delete_preview_btn}
          onClick={() => onClearClick(media.id)}
        >
          <i className={cls("ico ico-x")} />
        </span>
      )}
    </div>
  );
}

const AttachmentPreview = ({
  uploadedMedia = [],
  onClearMedia,
  hasPermission,
  canSelectMedia,
  onSelectMedia,
  hasMultiSelect,
  canRemoveMedia,
  onReorderMedia,
  alreadySelectedData,
}: Props) => {
  const { dataFileIconHandler } = useDatafileIcons();
  const uploadError = useSelector(
    (state: RootState) => state.mediaLibraryData.error,
    shallowEqual,
  );

  const onClearClick = (index: string) => {
    if (onClearMedia) {
      onClearMedia(index);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = uploadedMedia.findIndex((m) => m.id === active.id);
      const newIndex = uploadedMedia.findIndex((m) => m.id === over?.id);
      const reordered = arrayMove(uploadedMedia, oldIndex, newIndex);
      if (onReorderMedia) {
        onReorderMedia(reordered);
      }
    }
  };

  const renderImage = (media: MediaModel) => {
    if (!media.processed) {
      return <Loading isLoading loadType="spinner" />;
    } else {
      if (media.object === "video") {
        return <VideoAttachment attachment={media} />;
      } else if (media.object === "image" && media.originalUrl) {
        return (
          <img src={media.originalUrl} alt="image" className={styles.preview} />
        );
      } else {
        return (
          <img
            src={dataFileIconHandler(media.extension || media.object)}
            alt="media"
            className={styles.preview}
          />
        );
      }
    }
  };

  if (hasMultiSelect) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={uploadedMedia.map((m) => m.id)}
          strategy={rectSortingStrategy}
        >
          <div className={styles.container}>
            {uploadedMedia?.map((media: MediaModel) => (
              <SortableMediaItem
                key={media.id}
                media={media}
                renderImage={renderImage}
                onClearMedia={onClearMedia}
                canRemoveMedia={canRemoveMedia}
                uploadError={uploadError}
                onClearClick={onClearClick}
                cls={cls}
                styles={styles}
              />
            ))}
            {uploadedMedia?.length > 0 && (
              <AddMoreMedia
                hasMultiSelect={hasMultiSelect}
                hasPermission={hasPermission}
                canSelectMedia={canSelectMedia}
                onSelectMedia={onSelectMedia}
                alreadySelected={alreadySelectedData}
              />
            )}
          </div>
        </SortableContext>
      </DndContext>
    );
  }

  return (
    <div className={styles.container}>
      {uploadedMedia?.map((media: MediaModel) => (
        <div key={media.id} className={styles.preview_wrapper}>
          {!uploadError && renderImage(media)}
          {onClearMedia && canRemoveMedia && (
            <button
              className={styles.delete_preview_btn}
              onClick={() => onClearClick(media?.id)}
            >
              ✕
            </button>
          )}
        </div>
      ))}
      {hasMultiSelect && uploadedMedia?.length > 0 && (
        <AddMoreMedia
          hasMultiSelect={hasMultiSelect}
          hasPermission={hasPermission}
          canSelectMedia={canSelectMedia}
          onSelectMedia={onSelectMedia}
          alreadySelected={alreadySelectedData}
        />
      )}
    </div>
  );
};

export default AttachmentPreview;
